import { createTheme } from '@material-ui/core';
import hexToRgb from '../helpers/ui/coloringHelper';

export const efesBlue = '#154a94';
export const efesLightBlue = '#117FD1';
export const efesDarkBlue = '#2c2738';
export const efesTealGreen = '#3fdfc0';
export const efesGreen = '#159460';
export const efesDarkGray = '#4a4a4a';
export const efesGray = '#c6c6c6';
export const efesGrayAlt = '#b2b2b2';
export const efesLight = '#e8e8e8';
export const efesBrown = '#600000';
export const efesError = '#ff2400';
export const efesErrorBackground = '#fdf7f8';
export const efesOrange = '#f1613a';
export const efesBackgroundLight = '#f9fafb';
export const efesBorder = '#959caa';
export const efesGrayish = '#959ca9';
export const efesBlueish = '#00669d';
export const black = '#000';
export const white = '#fff';
export const iconGray = '#bcbcbc';
export const aliceGray = '#f8f9fb';
export const aliceGrayAlt = '#f2f3f4';
export const navbarHover = '#698aba';
export const bottomSheetShadow = '#00000017';
export const boxShadow = '#2C27380A';
export const inputBorder = '#707070';
export const inputPlaceholder = '#909090';
export const defaultPressed = '#092C5D';
export const defaultHover = '#123C76';
export const inheritPressed = '#0B6540';
export const inheritHover = '#108052';
export const primaryPressed = '#600000';
export const primaryHover = '#ce4925';
export const formSectionBorder = '#e3e3e3';
export const formSectionBackground = '#959daa';
export const venetianRed = '#be061b';
export const rangeGray = '#e9ecf0';

const MuiTheme = createTheme({
  typography: {
    allVariants: {
      fontFamily: 'Montserrat, sans-serif',
    },
  },
  props: {
    MuiButtonBase: {
      disableRipple: true,
      disableTouchRipple: true,
    },
  },
  shadows: Array(25).fill('none'),
  zIndex: {
    popover: 1301,
  },
  palette: {
    primary: {
      main: efesOrange,
      pressed: primaryPressed,
      hover: primaryHover,
    },
    secondary: {
      main: black,
      pressed: black,
      hover: black,
    },
    default: {
      main: efesBlue,
      pressed: defaultPressed,
      hover: defaultHover,
    },
    inherit: {
      main: efesGreen,
      pressed: inheritPressed,
      hover: inheritHover,
    },
    error: {
      main: efesError,
      background: efesErrorBackground,
      remove: venetianRed,
    },
    success: {
      main: efesGreen,
      efesTealGreen,
    },
    info: {
      main: '#11c5db',
    },
    warning: {
      main: '#dfae3f',
    },
    black: {
      main: black,
    },
    blue: {
      efesDarkBlue,
      efesLightBlue,
    },
    brown: {
      main: efesBrown,
    },
    gray: {
      efesLight,
      aliceGray,
      aliceGrayAlt,
      efesGray,
      efesGrayAlt,
      efesBorder,
      efesDarkGray,
      efesGrayish,
      inputPlaceholder,
      boxShadow,
      formSectionBorder,
      formSectionBackground,
      rangeGray,
    },
    border: {
      inputBorder,
    },
    white: {
      main: white,
      efesBackgroundLight,
    },
    helpers: {
      primary: '#3c44b1',
      main: 'rgba(25, 46, 91, .035)',
    },
    contrastThreshold: 3,
    tonalOffset: 0.1,
  },
  breakpoints: {
    values: {
      xs: 375,
      sm: 576,
      md: 768,
      tabletLandscape: 1024,
      lg: 1200,
      xl: 1381,
    },
  },

  overrides: {
    MuiPickersBasePicker: {
      pickerView: {
        background: `rgba(${hexToRgb(efesBackgroundLight)}, 0.5)`,
      },
    },
    MuiDialog: {
      root: {
        zIndex: '1400 !important',
      },
    },
    MuiCssBaseline: {
      '@global': {
        fontFamily: 'Montserrat, sans-serif',
      },
    },
    MuiInputLabel: {
      outlined: {
        transform: 'translate(14px, 17px) scale(1)',
        color: inputPlaceholder,
      },
      root: {
        fontWeight: 'normal',
        color: inputPlaceholder,
        '&$focused': {
          color: inputPlaceholder,
        },
        '&$error': {
          color: inputPlaceholder,
        },
        '&$disabled': {
          color: '#bababa',
        },
      },
      shrink: {
        '&$shrink': {
          color: inputPlaceholder,
        },
      },
    },
    MuiMenu: {
      paper: {
        maxHeight: 300,
        maxWidth: 300,
        marginTop: 5,
        border: `1px solid ${efesGray}`,
        boxShadow: `0px 20px 20px ${boxShadow}`,
        borderRadius: 6,
      },
    },
    PrivateSwitchBase: {
      root: {
        padding: ' 0 5px 0 5px',
      },
    },
    MuiOutlinedInput: {
      input: {
        '&:-webkit-autofill': {
          '-webkit-box-shadow': '0 0 0 100px #fff inset',
          '-webkit-text-fill-color': '#000',
        },
        '&::placeholder': {
          color: inputPlaceholder,
        },
      },
      multiline: {
        '&$marginDense': {
          paddingBottom: 18,
          paddingTop: 18,
        },
      },
      root: {
        '&$focused $notchedOutline': {
          borderColor: inputPlaceholder,
          borderWidth: '1px',
        },
        padding: '3px 0',
        '& fieldset': {
          borderColor: efesBorder,
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderRadius: 0,
        },
        '&:hover $notchedOutline': {
          borderColor: efesBorder,
        },
        '&$error $notchedOutline': {
          borderColor: efesError,
        },
        '&$disabled $notchedOutline': {
          borderColor: '#dbdbdb',
          color: '#bababa',
        },
        '&$disabled': {
          borderColor: '#dbdbdb',
          color: '#bababa',
        },
      },
    },
    MuiIconButton: {
      root: {
        padding: 0,
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiFormLabel: {
      root: {
        marginTop: 3,
      },

    },
    MuiTab: {
      root: {
        fontWeight: 'bold',
      },
    },
    MuiMenuItem: {
      root: {
        backgroundColor: white,
        '&:hover': {
          backgroundColor: efesBackgroundLight,
        },
        '&:focus': {
          backgroundColor: efesBackgroundLight,
        },
        '&:active ': {
          backgroundColor: efesBackgroundLight,
        },
        '&.Mui-selected': {
          backgroundColor: efesBackgroundLight,
        },
      },
    },
    MuiList: {
      padding: {
        '&.MuiList-padding': {
          padding: 0,
        },
      },
    },
    MuiSelect: {
      root: {
        display: 'flex',
        flexWrap: 'wrap',
      },
    },
    MuiButton: {
      root: {
        textTransform: 'none',
        fontWeight: 'normal',
        fontSize: '1.125rem',
        boxShadow: 'none',
        transition: 'none',
        borderRadius: '0.5rem',
        padding: '10px 22px',
      },
    },
    MuiChip: {
      root: {
        backgroundColor: `rgba(${hexToRgb(efesOrange)}, 0.1)`,
        color: efesOrange,
        height: 36,
        fontSize: '1rem',
        marginRight: '.5rem',
        '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus': {
          backgroundColor: `rgba(${hexToRgb(efesOrange)}, 0.2)`,
        },
      },
    },
    MuiFormHelperText: {
      contained: {
        marginRight: 0,
        marginLeft: 0,
        marginTop: 0,
        fontSize: '0.8rem',
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: '#070919',
        padding: '8px 16px',
        fontSize: 13,
      },
      arrow: {
        color: '#070919',
      },
    },
    MuiCircularProgress: {
      root: {
        height: 25,
        width: 25,
      },
    },
    MuiStepContent: {
      root: {
        borderLeft: `1px solid ${efesGray}`,
        marginLeft: 0,
        marginTop: 0,
        paddingLeft: 10,
        paddingRight: 0,
      },
    },

    MuiStepConnector: {
      line: {
        borderColor: 'transparent',
      },
      vertical: {
        marginLeft: 0,
        marginTop: 0,
        borderLeft: (props) => (props.disabled ? `1px solid ${efesGray}` : `2px solid ${efesOrange}`),
      },
    },

    MuiStepper: {
      root: {
        padding: 0,
        paddingRight: 0,
        paddingLeft: 20,
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiStepLabel: {
      iconContainer: {
        paddingRight: 0,
      },
      label: {
        color: black,
        fontSize: 16,
      },
    },
    MuiTypography: {
      body1: {
        color: black,
      },
    },

    MuiAutocomplete: {
      inputRoot: {
        '& .MuiAutocomplete-input': {
          minWidth: 0,
        },
      },
    },
  },
});

export default MuiTheme;

export const MuiAdditionalConfigs = {
  fullWidth: {
    xs: 12,
    sm: 12,
    md: 12,
    xl: 12,
  },
};
